/**
 * Ancla de vuelta a la tarjeta hija de una OT.
 *
 * Se escribe al ENTRAR en el detalle de una hija y se consume UNA sola vez al
 * montar la OT. Así cubre las dos puertas de vuelta con un solo mecanismo: el
 * botón «Volver» (que hace PUSH, o sea entrada de historial nueva, sin nada que
 * restaurar) y la flecha atrás del navegador (POP, donde la restauración nativa
 * no llega a tiempo porque la ruta pinta primero el spinner y el documento mide
 * casi nada en ese instante).
 *
 * 🔴 **Leer y borrar son dos funciones a propósito.** El consumidor necesita el
 * ancla durante el RENDER (la tarjeta tiene que nacer ya desplegada), y la app
 * monta bajo `React.StrictMode`, que invoca el render y los inicializadores de
 * `useState` DOS veces para cazar justo esto: si leer borrase, la segunda
 * invocación encontraría el almacén vacío y ganaría el `null` — verificado en
 * vivo el 2026-09-14, la tarjeta volvía cerrada y sin desplazar. `peek` es puro
 * y se puede llamar las veces que haga falta; `clear` vive en un efecto.
 *
 * Todo acceso va en try/catch: el ancla es un extra y jamás debe romper la
 * pantalla si el almacenamiento está bloqueado (ventana privada, WebView con
 * datos restringidos).
 */
const STORAGE_KEY = 'rois-ot-child-anchor';

type ChildCardAnchor = {
  parentOrderId: string;
  roid: string;
};

export function rememberChildCardAnchor(
  parentOrderId: string | undefined,
  roid: string | undefined
): void {
  if (!parentOrderId || !roid) return;

  try {
    const anchor: ChildCardAnchor = { parentOrderId, roid };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
  } catch {
    // Almacenamiento lleno o bloqueado: sin ancla, todo sigue como hoy.
  }
}

/** Lectura PURA: devuelve el roid anclado para esa OT, sin tocar el almacén. */
export function peekChildCardAnchor(
  parentOrderId: string | undefined
): string | null {
  if (!parentOrderId) return null;

  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ChildCardAnchor> | null;
    if (!parsed || parsed.parentOrderId !== parentOrderId) return null;
    return parsed.roid ?? null;
  } catch {
    return null;
  }
}

/**
 * Borra el ancla. Se llama desde un efecto, tras haberla usado — y también
 * cuando no casa con esta OT: un ancla que no corresponde a la pantalla en la
 * que estás ya es basura, porque solo se puede volver a la OT de la que saliste.
 */
export function clearChildCardAnchor(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Mismo motivo que arriba.
  }
}
